import React, { useCallback, useContext, useEffect, useState } from "react";
import { PlaidLink } from "react-plaid-link";
import {
  Button,
  Heading,
  Pane,
  SelectField,
  Label,
  toaster,
} from "evergreen-ui";
import ControlHubContext from "../../contexts/controlhub";

const ConfigureBankButton = ({ billingInfo, service, setLoadingState }) => {
  const { currentCompanyId } = useContext(ControlHubContext);
  const [linkToken, setLinkToken] = useState(null);
  const [plaidEnv, setPlaidEnv] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [accountId, setAccountId] = useState("");
  const [accountSelected, setAccountSelected] = useState(null);

  const handleOnExit = useCallback((data) => {
    console.log("ON EXIT", data);
  }, []);

  const handleOnSuccess = useCallback(
    async (publicToken, bankInfo) => {
      const { accounts } = bankInfo;
      const filteredAccounts = accounts.filter(
        (account) =>
          account.subtype === "checking" || account.subtype === "savings"
      );

      setAccounts(filteredAccounts);

      if (filteredAccounts.length > 0) {
        setAccountId(filteredAccounts[0].id);
      }

      await service.generatePlaidAccessToken(publicToken, currentCompanyId);
    },
    [currentCompanyId, service]
  );

  const submitBankAccount = useCallback(async () => {
    setLoadingState(true);
    try {
      await service.submitBankAccount(accountId, currentCompanyId);

      window.location.reload();
    } catch (error) {
      setLoadingState(false);

      toaster.danger(
        "We couldn't connect to your bank account, please refresh the page and retry. If the problem persists contact ControlHub for support"
      );
    }
  }, [accountId, currentCompanyId, service, setLoadingState]);

  useEffect(() => {
    if (!accountId) return;

    const accountTmp = accounts.find((account) => account.id === accountId);

    setAccountSelected(accountTmp);
  }, [accountId, accounts]);

  useEffect(() => {
    if (!billingInfo || !currentCompanyId) return;

    const generatePlaidLinkToken = async () => {
      const response = await service.generatePlaidLinkToken(currentCompanyId);
      const { link_token, env } = response;

      setLinkToken(link_token);
      setPlaidEnv(env);
    };

    const fetchPlaidAccounts = async () => {
      const accounts = await service.fetchPlaidAccounts(currentCompanyId);

      setAccounts(accounts);

      if (accounts.length > 0) {
        setAccountId(accounts[0].id);
      }
    };

    if (billingInfo.has_plaid_access_token) {
      fetchPlaidAccounts();
      return;
    }

    generatePlaidLinkToken();
  }, [billingInfo, currentCompanyId, service]);

  if (
    billingInfo &&
    (billingInfo.status !== "OK" || !billingInfo.requires_plaid_integration)
  )
    return <Pane />;

  return (
    <Pane
      maxWidth="33%"
      marginTop={20}
      border="muted"
      padding={20}
      elevation={0}
    >
      <Heading size={500} marginBottom={10}>
        Bank Account Connection
      </Heading>
      {linkToken &&
        plaidEnv &&
        !accounts &&
        !billingInfo.has_plaid_access_token && (
          <PlaidLink
            env={plaidEnv}
            product={["transactions", "auth"]}
            token={linkToken}
            onExit={handleOnExit}
            onSuccess={handleOnSuccess}
            className="plaid-start-button"
          >
            Connect Bank Account
          </PlaidLink>
        )}
      {accounts && accounts.length > 1 && (
        <SelectField
          onChange={(event) => setAccountId(event.target.value)}
          value={accountId}
          label="Select the Checking account to connect"
        >
          {accounts.map((account) => (
            <option value={account.id} key={account.id}>
              {account.name}
            </option>
          ))}
        </SelectField>
      )}

      {accountSelected && (
        <Pane>
          <Pane display="flex" justifyContent="space-between" marginBottom={20}>
            <Label size={300}> {accountSelected.name}</Label>
            <Heading size={300}> {`**** **** ${accountSelected.mask}`}</Heading>
          </Pane>
        </Pane>
      )}

      {accountId && (
        <Button marginTop={20} appearance="primary" onClick={submitBankAccount}>
          Connect this account
        </Button>
      )}
    </Pane>
  );
};

export default ConfigureBankButton;
